import { MODULE_REGISTRY, ModuleRouteMeta } from './module-registry';
import { NavItem, NavPermissions } from './nav-item';
import { parsePermissions } from './permissions';
import { UserModule } from './user-module';

/** Type alias for any registry shape compatible with the mapper. */
export type ModuleRegistry = Readonly<Record<string, ModuleRouteMeta>>;

const GROUP_ICON = '';
const DEFAULT_WEIGHT = 100;

const ALWAYS_TRUE_PERMISSIONS: NavPermissions = {
  read: true,
  write: false,
  update: false,
  delete: false,
};

/**
 * Modules whose permissions can be present in the API payload but should
 * never surface as a header-menu entry. Mirrors the legacy
 * `value.name !== "profile"` short-circuit in `fnLoopNavigation`
 * (`fb-shipper-frontend/src/app/services/index.util.service.js` line 1372).
 * Profile-style entries belong in the user-avatar dropdown, not the menubar.
 */
const HIDDEN_FROM_MENUBAR = new Set<string>(['profile']);

/**
 * Internal tree-builder shape — `children` is mutable here so we can
 * accumulate, then frozen into `readonly` when emitted as `NavItem`.
 */
interface MutableNavItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  permissions: NavPermissions;
  isPaid: boolean;
  weight: number;
  insertionOrder: number;
  children: MutableNavItem[];
}

/**
 * Pure mapper: converts the API-shaped `user_modules` array into the
 * **nested** UI-ready `NavItem[]` consumed by the header menubar (and other
 * permission-driven menus — e.g. the `+ New` create dropdown — by passing a
 * different `registry`).
 *
 * Algorithm (mirrors legacy `getNavList` / `fnLoopNavigation` semantics in
 * `fb-shipper-frontend/src/app/services/index.util.service.js`):
 *
 *   1. Filter inputs by `read` permission and validity.
 *   2. **Strict allowlist** — modules absent from the supplied `registry` are
 *      dropped (matches legacy `futureNav` behavior). `profile` is also
 *      explicitly excluded.
 *   3. Walk the dot-delimited `parent` path; create group nodes lazily so
 *      each ancestor exists before the leaf is appended.
 *   4. Group weights inherit the minimum weight of their descendants — so
 *      "Analytics" sits at the slot of its earliest child even though no
 *      explicit weight is configured for the group itself.
 *   5. Sort siblings by `weight` ascending; ties resolved by input order.
 *
 * The resulting tree is shape-compatible with PrimeNG's `MenuItem[]` after a
 * trivial mapping in the consuming component.
 *
 * @param modules — API `user_modules` array.
 * @param registry — optional override for the module map; defaults to the
 *   main `MODULE_REGISTRY`. Pass `CREATE_MENU_REGISTRY` for the `+ New`
 *   dropdown, etc.
 */
export function userModulesToNavItems(
  modules: readonly UserModule[],
  registry: ModuleRegistry = MODULE_REGISTRY,
): NavItem[] {
  if (!Array.isArray(modules) || modules.length === 0) return [];

  const root: MutableNavItem[] = [];
  const groupCache = new Map<string, MutableNavItem>();
  let insertionCursor = 0;

  for (const m of modules) {
    if (!m || typeof m.name !== 'string' || m.name.length === 0) continue;
    if (HIDDEN_FROM_MENUBAR.has(m.name)) continue;

    const meta = registry[m.name];
    if (!meta) continue;

    const permissions = parsePermissions(m.permissions);
    if (!permissions.read) continue;

    const parentPath = meta.parent ?? '';
    const parentChildren = parentPath ? ensureGroupChain(parentPath, root, groupCache, () => insertionCursor++) : root;
    const isPaid = m.is_paid ?? false;

    // Synthetic-parent fan-out: emit each virtual child under the same parent
    // path *instead of* the source leaf (mirrors legacy `fnLoopNavigation`'s
    // `configuration → companyInformation + shipmentStatus` split).
    if (meta.virtualChildren && meta.virtualChildren.length > 0) {
      for (const child of meta.virtualChildren) {
        parentChildren.push({
          id: child.id,
          label: child.label,
          icon: child.icon,
          route: child.route,
          permissions,
          isPaid,
          weight: child.weight,
          insertionOrder: insertionCursor++,
          children: [],
        });
      }
      continue;
    }

    parentChildren.push({
      id: m.name,
      label: meta.label ?? m.title ?? m.name,
      icon: meta.icon,
      route: meta.route,
      permissions,
      isPaid,
      weight: meta.weight ?? DEFAULT_WEIGHT,
      insertionOrder: insertionCursor++,
      children: [],
    });
  }

  propagateGroupWeights(root);
  return freezeAndSort(root);
}

/**
 * Walks `parent_title` and creates each missing group node along the way,
 * returning the children array of the deepest group so the caller can append
 * its leaf there. Cached so repeated paths reuse the same group nodes.
 */
function ensureGroupChain(
  path: string,
  root: MutableNavItem[],
  cache: Map<string, MutableNavItem>,
  nextOrder: () => number,
): MutableNavItem[] {
  const segments = path
    .split('.')
    .map((s) => s.trim())
    .filter(Boolean);
  let parentChildren = root;
  let pathSoFar = '';

  for (const segment of segments) {
    pathSoFar = pathSoFar ? `${pathSoFar}.${segment}` : segment;
    let group = cache.get(pathSoFar);
    if (!group) {
      group = {
        id: pathToId(pathSoFar),
        label: segment,
        icon: GROUP_ICON,
        route: '',
        permissions: ALWAYS_TRUE_PERMISSIONS,
        isPaid: false,
        weight: DEFAULT_WEIGHT,
        insertionOrder: nextOrder(),
        children: [],
      };
      cache.set(pathSoFar, group);
      parentChildren.push(group);
    }
    parentChildren = group.children;
  }
  return parentChildren;
}

function pathToId(path: string): string {
  return `group:${path
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;
}

/**
 * Bottom-up pass: every non-leaf node inherits the minimum weight of its
 * descendants. Lets a registered module's weight bubble up to position its
 * implicit parent group at the right slot in the menubar (e.g. `dashboard`
 * with weight 1 → "Analytics" group rendered before weight-2 "Rate Search").
 */
function propagateGroupWeights(nodes: MutableNavItem[]): void {
  for (const n of nodes) {
    if (n.children.length === 0) continue;
    propagateGroupWeights(n.children);
    const minChildWeight = n.children.reduce((min, c) => Math.min(min, c.weight), Number.POSITIVE_INFINITY);
    if (Number.isFinite(minChildWeight)) {
      n.weight = Math.min(n.weight, minChildWeight);
    }
  }
}

/**
 * Recursively sorts every sibling array by (weight asc, insertionOrder asc)
 * and converts the mutable tree into the public read-only `NavItem` shape.
 */
function freezeAndSort(nodes: MutableNavItem[]): NavItem[] {
  return nodes
    .slice()
    .sort((a, b) => a.weight - b.weight || a.insertionOrder - b.insertionOrder)
    .map((n) => {
      const item: NavItem = n.children.length
        ? {
            id: n.id,
            label: n.label,
            icon: n.icon,
            route: n.route,
            permissions: n.permissions,
            isPaid: n.isPaid,
            children: freezeAndSort(n.children),
          }
        : {
            id: n.id,
            label: n.label,
            icon: n.icon,
            route: n.route,
            permissions: n.permissions,
            isPaid: n.isPaid,
          };
      return item;
    });
}
